import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, Star, Trash2 } from "lucide-react";
import { useState } from "react";
import { bankAccountValidator } from "shared";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAppForm } from "@/hooks/useCreateForm";
import { queryClient, type RouterOutputs, trpc } from "@/lib/trpcClient";

type BankAccountType = RouterOutputs["payment"]["listBankAccounts"][number];

export const Route = createFileRoute("/_authed/_org/settings/bank")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data: bankAccounts } = useSuspenseQuery(
    trpc.payment.listBankAccounts.queryOptions(),
  );

  const { mutate: createBankAccount, isPending: isCreating } = useMutation(
    trpc.payment.createBankAccount.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: async () => {
        await queryClient.refetchQueries(
          trpc.payment.listBankAccounts.queryOptions(),
        );
        toast.success("Bank account added successfully");
        form.reset();
      },
    }),
  );

  const { mutate: setDefaultAccount, isPending: isSettingDefaultAccount } =
    useMutation(
      trpc.payment.setAsDefault.mutationOptions({
        onError: (error) => {
          toast.error(error.message);
        },
        onSuccess: async () => {
          await queryClient.refetchQueries(
            trpc.payment.listBankAccounts.queryOptions(),
          );

          toast.success("Default bank account updated successfully");
        },
      }),
    );

  const form = useAppForm({
    defaultValues: {
      accountNumber: "",
      name: "",
      routingNumber: "",
    },
    onSubmit: async ({ value }) => {
      createBankAccount(value);
    },
    validators: {
      onSubmit: bankAccountValidator,
    },
  });

  return (
    <div className="flex max-w-5xl flex-col gap-6">
      <div>
        <h2 className="font-semibold text-xl">Bank Accounts</h2>
        <p className="text-neutral-600 text-sm">
          Manage your bank accounts for reimbursements. Set a favorite account
          to be used by default.
        </p>
      </div>

      {/* Existing Bank Accounts */}
      {bankAccounts && bankAccounts.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-medium">Your Accounts</h3>
          <div className="grid gap-4">
            {bankAccounts.map((account) => (
              <Card
                key={account.id}
                className={`relative ${account.isDefault ? "ring-2 ring-blue-500" : ""}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{account.name}</CardTitle>
                    <div className="flex items-center gap-2">
                      {account.isDefault ? (
                        <div className="flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-blue-700 text-xs">
                          <Star className="size-3 fill-current" />
                          Favorite
                        </div>
                      ) : (
                        <Button
                          onClick={() =>
                            setDefaultAccount({ accountId: account.id })
                          }
                          disabled={isSettingDefaultAccount}
                          size="sm"
                          variant="outline"
                        >
                          {isSettingDefaultAccount ? (
                            <Loader2 className="size-3 animate-spin" />
                          ) : (
                            <Star className="size-3" />
                          )}
                          Set as favorite
                        </Button>
                      )}

                      <DeleteAccountButton account={account} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-neutral-600">Account Number</p>
                      <p className="font-mono">***{account.last4}</p>
                    </div>
                    <div>
                      <p className="text-neutral-600">Routing Number</p>
                      <p className="font-mono">{account.routingNumber}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Add New Bank Account */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="size-4" />
            Add Bank Account
          </CardTitle>
          <CardDescription>
            Add a new bank account for receiving reimbursements.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit(e);
            }}
          >
            <div className="space-y-4">
              <form.AppField
                children={(field) => (
                  <field.TextField label="Account Nickname" placeholder="" />
                )}
                name="name"
              />

              <div className="flex flex-col gap-4 sm:flex-row [&>div]:self-start">
                <form.AppField
                  children={(field) => (
                    <field.TextField
                      label="Account Number"
                      placeholder=""
                      type="number"
                    />
                  )}
                  name="accountNumber"
                />

                <form.AppField
                  children={(field) => (
                    <field.TextField
                      label="Routing Number"
                      placeholder=""
                      type="number"
                    />
                  )}
                  name="routingNumber"
                />
              </div>
            </div>

            <form.Subscribe
              children={(state) => (
                <div className="mt-6 flex gap-2">
                  <Button
                    onClick={() => form.reset()}
                    type="button"
                    variant="outline"
                    disabled={state.isDefaultValue}
                  >
                    <Trash2 className="size-4" />
                    Cancel
                  </Button>
                  <Button
                    disabled={isCreating || !state.canSubmit}
                    type="submit"
                  >
                    {isCreating && <Loader2 className="size-4 animate-spin" />}
                    Add Bank Account
                  </Button>
                </div>
              )}
              selector={(state) => state}
            />
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function DeleteAccountButton({ account }: { account: BankAccountType }) {
  const { mutate: deleteBankAccount, isPending: isDeletingBankAccount } =
    useMutation(
      trpc.payment.deleteBankAccount.mutationOptions({
        onError: (error) => {
          toast.error(error.message);
        },
        onSuccess: async () => {
          await queryClient.refetchQueries(
            trpc.payment.listBankAccounts.queryOptions(),
          );
          toast.success("Bank account deleted successfully");
        },
      }),
    );

  const pendingReimbursementCount = account.reimbursements.filter(
    (r) => r.request.status === "pending",
  ).length;

  const [dialogOpen, setIfDialogOpen] = useState(false);
  const dialogDisabled = pendingReimbursementCount === 0;

  return (
    <AlertDialog
      open={dialogOpen}
      onOpenChange={(open) => {
        if (open && dialogDisabled) return;

        setIfDialogOpen(open);
      }}
    >
      <AlertDialogTrigger>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (dialogDisabled) deleteBankAccount({ accountId: account.id });
          }}
        >
          {isDeletingBankAccount ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Trash2 className="size-3.5 text-neutral-600" strokeWidth={1.5} />
          )}
          Delete
        </Button>
      </AlertDialogTrigger>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {account.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This account is tied to {pendingReimbursementCount} pending
            reimbursement request{pendingReimbursementCount !== 1 && "s"}.
            Deleting this account now will also remove it from any associated
            requests, which may affect your ability to receive payment through
            Givefront.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              onClick={() => deleteBankAccount({ accountId: account.id })}
            >
              Delete anyway
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
